'use client';

import { useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import SeverityBadge from '@/components/ui/SeverityBadge';
import StatusBadge from '@/components/ui/StatusBadge';
import { toast } from 'sonner';

interface IncidentDetailPanelProps {
  incident: any;
  onClose: () => void;
}

const timelineTypeConfig = {
  detection: { color: 'bg-red-500', line: 'border-red-500/30' },
  action: { color: 'bg-blue-500', line: 'border-blue-500/30' },
  update: { color: 'bg-zinc-500', line: 'border-zinc-600' },
  escalation: { color: 'bg-orange-500', line: 'border-orange-500/30' },
};

export default function IncidentDetailPanel({ incident, onClose }: IncidentDetailPanelProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-zinc-950 border-l border-zinc-800 z-50 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-zinc-800 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-semibold text-zinc-300">{incident.id}</span>
              <SeverityBadge severity={incident.severity} size="md" />
              <StatusBadge status={incident.status} size="md" />
              {incident.slaBreached && (
                <span className="flex items-center gap-1 text-[10px] font-semibold text-orange-400 bg-orange-500/10 border border-orange-500/20 px-1.5 py-0.5 rounded uppercase">
                  <Icon name="ExclamationTriangleIcon" size={10} />
                  SLA Breached
                </span>
              )}
            </div>
            <h2 className="text-sm font-semibold text-zinc-100 mt-1.5 leading-snug">{incident.title}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors ml-4 flex-shrink-0">
            <Icon name="XMarkIcon" size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin">
          {/* Key Metrics */}
          <div className="grid grid-cols-4 gap-0 border-b border-zinc-800">
            {[
              { label: 'First Seen', value: incident.firstSeen, mono: true },
              { label: 'MTTD', value: incident.mttd, mono: true },
              { label: 'Event Count', value: incident.eventCount.toLocaleString(), mono: true },
              { label: 'MITRE ID', value: incident.mitreId, mono: true },
            ].map((m, i) => (
              <div key={m.label} className={`px-4 py-3 ${i < 3 ? 'border-r border-zinc-800' : ''}`}>
                <p className="text-[10px] font-medium uppercase tracking-widest text-zinc-600">{m.label}</p>
                <p className={`text-sm font-semibold text-zinc-200 mt-0.5 ${m.mono ? 'font-mono' : ''}`}>{m.value}</p>
              </div>
            ))}
          </div>

          <div className="p-6 space-y-6">
            {/* Description */}
            <div>
              <h4 className="text-[11px] font-semibold uppercase tracking-widest text-zinc-600 mb-2">Summary</h4>
              <p className="text-sm text-zinc-400 leading-relaxed">{incident.description}</p>
            </div>

            {/* Assignee & MITRE */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 mb-2">Assigned Analyst</p>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 flex items-center justify-center text-xs font-bold text-white">
                    {incident.assigneeInitials}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-zinc-200">{incident.assignee}</p>
                    <p className="text-[11px] text-zinc-600">SOC Analyst</p>
                  </div>
                </div>
                <button
                  onClick={() => toast.success('Reassignment dialog would open here')}
                  className="mt-3 w-full text-xs text-zinc-500 hover:text-zinc-300 border border-zinc-800 hover:border-zinc-700 rounded-md py-1.5 transition-colors"
                >
                  Reassign
                </button>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 mb-2">MITRE ATT&CK</p>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-sm font-bold text-purple-400">{incident.mitreId}</span>
                </div>
                <p className="text-sm text-zinc-300">{incident.mitreTactic}</p>
                <a
                  href={`https://attack.mitre.org/techniques/${incident.mitreId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Icon name="ArrowTopRightOnSquareIcon" size={11} />
                  View in MITRE ATT&CK
                </a>
              </div>
            </div>

            {/* Affected Assets */}
            <div>
              <h4 className="text-[11px] font-semibold uppercase tracking-widest text-zinc-600 mb-2">Affected Assets ({incident.affectedAssets.length})</h4>
              <div className="flex flex-wrap gap-2">
                {incident.affectedAssets.map((asset: string) => (
                  <span key={asset} className="flex items-center gap-1.5 font-mono text-xs bg-zinc-900 border border-zinc-800 text-zinc-400 px-2.5 py-1.5 rounded-md">
                    <Icon name="ServerIcon" size={11} className="text-zinc-600" />
                    {asset}
                  </span>
                ))}
              </div>
            </div>

            {/* Timeline */}
            <div>
              <h4 className="text-[11px] font-semibold uppercase tracking-widest text-zinc-600 mb-3">Incident Timeline</h4>
              <div className="relative pl-5">
                {incident.timeline.map((entry: any, i: number) => {
                  const cfg = timelineTypeConfig[entry.type as keyof typeof timelineTypeConfig];
                  return (
                    <div key={i} className="relative pb-4 last:pb-0">
                      {i < incident.timeline.length - 1 && (
                        <div className={`absolute left-[-13px] top-3 bottom-0 w-px border-l ${cfg.line}`} />
                      )}
                      <div className={`absolute left-[-17px] top-1.5 w-2 h-2 rounded-full ${cfg.color} ring-2 ring-zinc-950`} />
                      <div className="flex items-start gap-3">
                        <span className="font-mono text-[11px] text-zinc-600 whitespace-nowrap pt-0.5">{entry.ts}</span>
                        <div className="flex-1">
                          <p className="text-xs text-zinc-300">{entry.action}</p>
                          <p className="text-[11px] text-zinc-600 mt-0.5">by {entry.user}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex-shrink-0 p-4 border-t border-zinc-800 flex items-center gap-3">
          <button
            onClick={() => { toast.success(`${incident.id} escalated to P0`); }}
            className="px-3 py-2 bg-red-600/20 border border-red-500/30 hover:bg-red-600/30 text-red-400 text-xs font-semibold rounded-lg transition-colors"
          >
            Escalate to P0
          </button>
          <button
            onClick={() => { toast.success(`${incident.id} marked as Contained`); }}
            className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-medium rounded-lg transition-colors"
          >
            Mark Contained
          </button>
          <button
            onClick={() => { toast.success(`${incident.id} resolved`); }}
            className="flex-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-lg transition-colors active:scale-95"
          >
            Mark Resolved
          </button>
        </div>
      </div>
    </>
  );
}